import type { SelectOption } from '@tryhackme/thm-ui-components';
import type { BaseUserProps } from '../../user.types';
import { useMemo, useState } from 'react';
import { faCalendar, faPlus, faTimer } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DescriptionListItem, TextField } from '@tryhackme/thm-ui-components';
import { CertificationType } from 'src/common/enums/certification';
import { CertificationActionButtons } from './certification-action-buttons';
import { formatDate, getPillColor } from './certification-details.helpers';
import { useLazyGetCertificationDetailsQuery } from './certification-details.slice';
import { StyledRolePill, StyledRolePills, StyledRow, StyledSubscriptionDetails, StyledTitle } from './certification-details.styles';
import type { GetPurchaseWithLatestAttemptResponse } from './certification-details.types';
export const CertificationDetails = ({
  user
}: BaseUserProps) => {
  const [selectedCertification, setSelectedCertification] = useState<string | null>(null);
  const isPurchaseSelected = selectedCertification ? (user.certifications ?? []).some(p => p._id === selectedCertification) : false;
  const [trigger, {
    data: certificationData,
    isLoading
  }] = useLazyGetCertificationDetailsQuery();
  const options = useMemo(() => {
    const userCertifications = (user.certifications ?? []).map(certification => ({
      label: certification.name,
      value: certification._id
    }));
    const purchasedTypes = new Set((user.certifications ?? []).map(cert => cert.certificationExamType));
    const types = Object.values(CertificationType).filter(type => !purchasedTypes.has(type)).map(type => ({
      label: type,
      value: type
    }));
    return [...userCertifications, ...types];
  }, [user.certifications]);
  const selectedOption = useMemo(() => options.find(option => option.value === selectedCertification) ?? null, [options, selectedCertification]);
  const handleSelection = (selectedValue: SelectOption | null) => {
    const value = selectedValue?.value ?? null;
    setSelectedCertification(value);
    const purchaseIds = new Set((user.certifications ?? []).map(p => p._id));
    const isPurchaseId = value ? purchaseIds.has(value) : false;
    if (isPurchaseId && value) {
      trigger({
        userId: user.id,
        purchaseId: value
      });
    }
  };
  return <StyledSubscriptionDetails data-sentry-element="StyledSubscriptionDetails" data-sentry-component="CertificationDetails" data-sentry-source-file="certification-details.tsx">
      <StyledTitle data-sentry-element="StyledTitle" data-sentry-source-file="certification-details.tsx">Certification details</StyledTitle>
      <TextField variant="select" isMulti={false} isSearchable={false} options={options} value={selectedOption} selected={selectedOption} handleSelection={handleSelection} placeholder="Select a certification" data-sentry-element="TextField" data-sentry-source-file="certification-details.tsx" />
      {selectedCertification && <>
          <CertificationActionButtons isPurchaseSelected={isPurchaseSelected} certificationData={certificationData} />
          {!isLoading && isPurchaseSelected && certificationData?.data && <CertificationLatestAttempt certificationData={certificationData.data} />}
        </>}
    </StyledSubscriptionDetails>;
};
export const CertificationLatestAttempt = ({
  certificationData
}: {
  certificationData: GetPurchaseWithLatestAttemptResponse;
}) => {
  const certificationState = certificationData.latestAttempt?.state || 'not_started';
  return <>
      <StyledRow data-sentry-element="StyledRow" data-sentry-source-file="certification-details.tsx">
        <DescriptionListItem label="State" value={<StyledRolePills>
              <StyledRolePill color={getPillColor(certificationState)}>{certificationState}</StyledRolePill>
            </StyledRolePills>} data-sentry-element="DescriptionListItem" data-sentry-source-file="certification-details.tsx" />
        <DescriptionListItem label="Section" value={certificationData.latestAttempt?.sections?.[certificationData.latestAttempt?.currentSection ?? 0]?.sectionName ?? '-'} data-sentry-element="DescriptionListItem" data-sentry-source-file="certification-details.tsx" />
      </StyledRow>
      <StyledRow data-sentry-element="StyledRow" data-sentry-source-file="certification-details.tsx">
        <DescriptionListItem label="Score" value={certificationData.latestAttempt?.result?.points?.toString() || '-'} data-sentry-element="DescriptionListItem" data-sentry-source-file="certification-details.tsx" />
        <DescriptionListItem label="Number of attempts" value={certificationData.purchase?.numberOfAttempts || 0} buttonOnClick={() => {}} buttonChildren={<>
              <FontAwesomeIcon icon={faPlus} />
              Add retake
            </>} data-sentry-element="DescriptionListItem" data-sentry-source-file="certification-details.tsx" />
      </StyledRow>
      <StyledRow data-sentry-element="StyledRow" data-sentry-source-file="certification-details.tsx">
        <DescriptionListItem label="Time started" value={formatDate(certificationData.latestAttempt?.timeStarted)} data-sentry-element="DescriptionListItem" data-sentry-source-file="certification-details.tsx" />
        <DescriptionListItem label="Time finished" value={formatDate(certificationData.latestAttempt?.willEndAt)} buttonOnClick={() => {}} buttonChildren={<>
              <FontAwesomeIcon icon={faTimer} />
              Extend time
            </>} data-sentry-element="DescriptionListItem" data-sentry-source-file="certification-details.tsx" />
      </StyledRow>
      <StyledRow data-sentry-element="StyledRow" data-sentry-source-file="certification-details.tsx">
        <DescriptionListItem label="Date of purchase" value={formatDate(certificationData.purchase?.createdAt)} data-sentry-element="DescriptionListItem" data-sentry-source-file="certification-details.tsx" />
        <DescriptionListItem label="Certification expiry date" value={formatDate(certificationData.purchase?.expiryDate)} buttonOnClick={() => {}} buttonChildren={<>
              <FontAwesomeIcon icon={faCalendar} />
              Change expiry date
            </>} data-sentry-element="DescriptionListItem" data-sentry-source-file="certification-details.tsx" />
      </StyledRow>
    </>;
};